import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { adminService } from '../../services/adminService';
import UserTable from './components/UserTable';
import UserFilters from './components/UserFilters';
import UserEditModal from './components/UserEditModal';
import UserStatsCards from './components/UserStatsCards';

const AdminUserManagementSystem = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { user, userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    adminUsers: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }

    // The profile loads asynchronously. Without this, the guard runs on the
    // first render while userProfile is still null, reads that as "not an
    // admin", and redirects — so admins could never deep-link or refresh
    // an admin page, only reach it by client-side navigation after load.
    if (!userProfile) return;

    if (!(userProfile?.isPlatformAdmin ?? userProfile?.roles?.includes('admin'))) {
      navigate('/user-dashboard');
      return;
    }

    loadUsers();
  }, [user, userProfile, navigate, filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filterParams = {
        search: filters?.search || undefined,
        roles: filters?.role ? [filters?.role] : undefined,
        isActive: filters?.status === 'active' ? true : filters?.status === 'inactive' ? false : undefined
      };
      
      const data = await adminService?.getAllUsers(filterParams);
      
      // Apply sorting
      const sorted = [...data]?.sort((a, b) => {
        const aVal = a?.[filters?.sortBy];
        const bVal = b?.[filters?.sortBy];
        
        if (filters?.sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setUsers(sorted);
      
      // Calculate stats
      setStats({
        totalUsers: data?.length,
        activeUsers: data?.filter(u => u?.isActive)?.length,
        inactiveUsers: data?.filter(u => !u?.isActive)?.length,
        adminUsers: data?.filter(u => u?.isPlatformAdmin ?? u?.roles?.includes('admin'))?.length
      });
    } catch (err) {
      setError(err?.message || t('userManagement.errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await adminService?.updateUserStatus(userId, !currentStatus);
      loadUsers();
    } catch (err) {
      setError(err?.message || t('userManagement.errors.failedToUpdateStatus'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window?.confirm(t('userManagement.confirmations.deleteUserWithData'))) {
      return;
    }

    try {
      await adminService?.deleteUser(userId);
      loadUsers();
    } catch (err) {
      setError(err?.message || t('userManagement.errors.failedToDelete'));
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleSaveUser = async (userId, updates) => {
    try {
      await adminService?.updateUserRoles(userId, updates?.roles);
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(err?.message || t('userManagement.errors.failedToUpdate'));
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleBulkAction = async (action, userIds) => {
    if (action === 'activate') {
      for (const userId of userIds) {
        await adminService?.updateUserStatus(userId, true);
      }
    } else if (action === 'deactivate') {
      for (const userId of userIds) {
        await adminService?.updateUserStatus(userId, false);
      }
    } else if (action === 'delete') {
      if (!window?.confirm(t('userManagement.confirmations.bulkDelete', { count: userIds?.length }))) {
        return;
      }
      for (const userId of userIds) {
        await adminService?.deleteUser(userId);
      }
    }
    loadUsers();
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      
      <div className="page-shell py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('userManagement.pageTitle')}</h1>
              <p className="text-muted-foreground">{t('userManagement.subtitle')}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/master-admin-dashboard')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              {t('userManagement.backToDashboard')}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <UserStatsCards stats={stats} />

        {/* Filters */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border-2 border-error rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-error font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-error hover:text-error/80">
              <Icon name="X" size={18} />
            </button>
          </div>
        )}

        {/* User Table */}
        <div className="bg-card border-2 border-border rounded-lg shadow-elevation-2">
          <UserTable
            users={users}
            loading={loading}
            onToggleStatus={handleToggleStatus}
            onDeleteUser={handleDeleteUser}
            onEditUser={handleEditUser}
            onBulkAction={handleBulkAction}
            filtered={Boolean(filters?.search || filters?.role || filters?.status)}
            onClearFilters={() => setFilters((f) => ({ ...f, search: '', role: '', status: '' }))}
          />
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
        />
      )}
    </div>
  );
};

export default AdminUserManagementSystem;